import React, { useState, useContext, useEffect, useRef, useMemo } from "react";
import { ResponsiveBarCanvas } from "@nivo/bar";
import { ProductsContext } from "../context/products.context";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import LeftSidebar from "./LeftSidebar";
import OptionsOutsideSchool from "./OptionsOutsideSchool";
import { useCipModal } from "../hooks/useCipModal";
import CipModalDialog from "./CipModalDialog";

export const Equity = () => {
  const { filterSession, defaultDistrict, setSelectedSchools, selectedSchools, nontrad } =
    useContext(ProductsContext);

  const { isOpen, openModal, closeModal } = useCipModal();

  const [equityGender, setEquityGender] = useState([]);
  const ref = useRef();

  const selectedSch = useMemo(() => selectedSchools.map((sch) => sch.value), [selectedSchools]);

  const onChangeSelection = (name) => {
    const newSelectedItems = selectedSchools.includes(name)
      ? selectedSchools.filter((item) => item !== name)
      : [...selectedSchools, name];
    setSelectedSchools(newSelectedItems);
  };

  useEffect(() => {
    const fetchEquityGender = async () => {
      if (defaultDistrict) {
        try {
          const docRef = doc(db, "equityGender_WA_2025", defaultDistrict.value);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setEquityGender(docSnap.data().equityGender);
          } else {
            console.warn(`Document for district ${defaultDistrict.value} does not exist.`);
            setEquityGender([]);
          }
        } catch (error) {
          console.error("Error fetching equityGender data:", error);
          setEquityGender([]);
        }
      }
    };
    fetchEquityGender();
  }, [defaultDistrict]);

  const newGender = useMemo(() => {
    if (!equityGender || equityGender.length === 0) return [];

    return equityGender
      .map((item) => {
        // Handle non-traditional filters
        if (filterSession === "nontrad-female") {
          if (!nontrad.female.includes(item.cipcode)) return null;
        } else if (filterSession === "nontrad-male") {
          if (!nontrad.male.includes(item.cipcode)) return null;
        } else {
          // Standard cluster/program filter
          if (!(item.clusterName === filterSession || item.programArea === filterSession)) {
            return null;
          }
        }

        const maleAggregated = Array(6).fill(0);
        const femaleAggregated = Array(6).fill(0);

        item.enrollment.forEach((enrollment) => {
          if (selectedSch.includes(enrollment.schoolName)) {
            if (enrollment.Male && Array.isArray(enrollment.Male)) {
              enrollment.Male.forEach((count, index) => {
                maleAggregated[index] += count;
              });
            }

            if (enrollment.Female && Array.isArray(enrollment.Female)) {
              enrollment.Female.forEach((count, index) => {
                femaleAggregated[index] += count;
              });
            }
          }
        });

        return {
          cipcode: item.cipcode,
          cipcodeName: item.cipcodeName,
          male: maleAggregated,
          female: femaleAggregated,
        };
      })
      .filter(Boolean);
  }, [equityGender, filterSession, selectedSch, nontrad.female, nontrad.male]);

  // Count non-traditional CIPs with enrollment data
  const nontradCounts = useMemo(() => {
    if (!equityGender || equityGender.length === 0) return { female: 0, male: 0 };

    const cipsWithEnrollment = new Set(
      equityGender
        .filter((item) => {
          // Check if any school has enrollment
          return item.enrollment?.some((enrollment) =>
            selectedSch.includes(enrollment.schoolName) &&
            ((enrollment.Male?.some((v) => v > 0)) || (enrollment.Female?.some((v) => v > 0)))
          );
        })
        .map((item) => item.cipcode)
    );

    return {
      female: nontrad.female.filter((cip) => cipsWithEnrollment.has(cip)).length,
      male: nontrad.male.filter((cip) => cipsWithEnrollment.has(cip)).length,
    };
  }, [equityGender, selectedSch, nontrad.female, nontrad.male]);

  const axisBottom = {
    tickSize: 5,
    tickPadding: 5,
    tickRotation: 0,
    legend: "School Year",
    legendPosition: "middle",
    legendOffset: 32,
  };

  const theme = {
    axis: {
      fontSize: "14px",
      tickColor: "#9eb2d4",
      ticks: {
        line: {
          stroke: "#9eb2d4",
        },
        text: {
          fill: "#9eb2d4",
        },
      },
      legend: {
        text: {
          fill: "#9eb2d4",
        },
      },
    },
    grid: {
      line: {
        stroke: "#9eb2d4",
      },
    },
  };

  const legends = [
    {
      dataFrom: "keys",
      anchor: "bottom-right",
      direction: "column",
      justify: false,
      translateX: 120,
      translateY: 0,
      itemsSpacing: 2,
      itemWidth: 100,
      itemHeight: 20,
      itemDirection: "left-to-right",
      itemOpacity: 0.85,
      itemTextColor: "#ffffff",
      symbolSize: 20,
      effects: [
        {
          on: "hover",
          style: {
            itemOpacity: 1,
          },
        },
      ],
    },
  ];

  const generateChartData = (male, female) => {
    const data = [];
    for (let i = 0; i < 5; i++) {
      const yearLabel = 2021 + i;
      const maleCount = male[i] || 0;
      const femaleCount = female[i] || 0;
      const total = maleCount + femaleCount;

      if (maleCount === 0 && femaleCount === 0) {
        data.push({
          map: yearLabel,
          female: null,
          "female (highlight)": null,
          male: null,
          "male (highlight)": null,
          femaleNumerator: 0,
          maleNumerator: 0,
          allDenominator: 0,
        });
        continue;
      }

      const femalePercentage = total > 0 ? femaleCount / total : 0;
      const malePercentage = total > 0 ? maleCount / total : 0;

      const femaleHighlight =
        femalePercentage <= 0.25 || femalePercentage >= 0.75 ? femalePercentage : null;
      const maleHighlight =
        malePercentage <= 0.25 || malePercentage >= 0.75 ? malePercentage : null;

      data.push({
        map: yearLabel,
        female: femaleHighlight === null ? femalePercentage : null,
        "female (highlight)": femaleHighlight,
        male: maleHighlight === null ? malePercentage : null,
        "male (highlight)": maleHighlight,
        femaleNumerator: femaleCount,
        maleNumerator: maleCount,
        allDenominator: total,
      });
    }
    return data;
  };

  const refsLayer = () => (
    <g>
      {/* Add any custom SVG elements here */}
    </g>
  );

  return (
    <div className="max-w-[2000px] pt-4">
      <div className="mx-auto px-4">
        {/* Left Sidebar */}
        <div className="bg-white border-gray-100 border-r-2 fixed z-20 mt-10 inset-0 top-[3.8125rem] left-4 right-auto w-[19.5rem] pb-10 px-8 overflow-y-auto">
          <LeftSidebar nontradFemaleCount={nontradCounts.female} nontradMaleCount={nontradCounts.male} />
        </div>
         {/* Main Content */}
         <div className="pl-[360px]">
          <header className="items-center gap-6">
            <h1 className="mt-2 text-4xl font-bold text-slate-900">Equity by Gender</h1>
          </header>
          
          <div className="space-x-6">
            <OptionsOutsideSchool isMulti closeMenuOnSelect={false} />
            <hr className="mb-12" />
          </div>

          {/* Charts Container */}
          <div className="flex flex-wrap mx-auto pt-10">
            {newGender.map((item, index) => (
              <div
                key={`${item.cipcode}-${index}`} // Ensures uniqueness even if cipcode duplicates
                className="flex flex-row h-[270px] mb-[100px] mx-3 cursor-pointer"
                onClick={() => openModal(item.cipcode)}
              >
                <div className="bg-sky-50 relative rounded-t-xl group hover:border-sky-100 border-4 border-white">
                  {/* CIP Code Header */}
                  <button title={item.cipcodeName} className="flex w-[320px] h-[72px]">
                    {/* Non-Traditional Badge */}
                    {(nontrad.female.includes(item.cipcode) || nontrad.male.includes(item.cipcode)) && (
                      <div
                        key={`badge-${item.cipcode}-${index}`} // Assign a key if badges are part of a list
                        title="Non Traditional"
                        className="absolute -top-3 -left-2 w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600"
                      >
                        <span className="text-xs font-bold">NT</span>
                      </div>
                    )}
                    {/* CIP Code Details */}
                    <div className="inline-flex group ml-4 mt-4 items-top">
                      <div className="w-[5px] h-[18px] mx-2 mt-1 bg-[#3C43FF]"></div>
                      <div className="text-left w-[250px]">
    {/* CIP Code Name */}
    <p className="text-slate-900 text-ellipsis overflow-hidden whitespace-nowrap">
      {item.cipcodeName}
    </p>
    {/* CIP Code and Non-Traditional Status */}
    {item.cipcode && (
      <p className="text-slate-700 text-sm whitespace-nowrap">
        {item.cipcode}{" "}
        {nontrad.female.includes(item.cipcode)
          ? "(Non-Traditional: Female)"
          : nontrad.male.includes(item.cipcode)
          ? "(Non-Traditional: Male)"
          : ""}
      </p>
    )}
  </div>
                    </div>
                  </button>

                  {/* Chart Container */}
                  <div className="bg-sky-50" style={{ height: 188, width: 320 }}>
                    <ResponsiveBarCanvas
                      ref={ref}
                      width={320}
                      height={188}
                      margin={{ top: 0, right: 0, bottom: 25, left: 0 }}
                      data={generateChartData(item.male, item.female)}
                      keys={["female", "male", "female (highlight)", "male (highlight)"]}
                      groupMode="stacked"
                      indexBy="map"
                      valueFormat={(value) => `${(Math.abs(Number(value * 100)).toFixed(0))}%`}
                      minValue={0}
                      maxValue={1.1}
                      labelTextColor="black"
                      labelSkipWidth={0}
                      labelSkipHeight={0}
                      enableGridX={false}
                      enableGridY={false}
                      axisBottom={axisBottom}
                      axisLeft={false}
                      borderRadius={0}
                      borderWidth={22}
                      theme={theme}
                      padding={0.35}
                      innerPadding={0}
                      yScale={{ type: "linear", stacked: true }}
                      
                      // Color Mapping: Original orange colors for highlights
                      colors={({ id }) => {
                        if (id === "female (highlight)") return "#ffad7ea7"; // Original orange for female highlight
                        if (id === "male (highlight)") return "#ffd2b9a1"; // Original orange for male highlight
                        return id === "female" ? "#58649ea2" : "#b9b8cea2"; // Original colors
                      }}
                      
                      legends={legends}
                      borderColor="rgba(255,255,255,0)"
                      
                      // Custom Tooltip
                      tooltip={({ id, value, data, color }) => {
                        // Prevent tooltip from showing if value is null or zero
                        if (value === null || value === 0) return null;

                        return (
                          <div className="bg-white relative p-2 rounded-md flex-col top-[100px] w-[250px] left-[145px] shadow-lg shadow-sky-400/10 text-left border-slate-200 border">
                            <div className="flex items-center">
                              <div style={{ background: color, width: "12px", height: "12px", borderRadius: "100%" }} />
                              <span className="text-slate-600 ml-2 capitalize">{`${id}, ${data.map}`}</span>
                            </div>
                            {(id === "female" || id === "female (highlight)") && (
                              <span>
                                <strong>{`${(Math.abs(Number(value * 100)).toFixed(2))}% `}</strong>
                                {`(${data.femaleNumerator.toLocaleString()} of ${data.allDenominator.toLocaleString()})`}
                              </span>
                            )}
                            {(id === "male" || id === "male (highlight)") && (
                              <span>
                                <strong>{`${(Math.abs(Number(value * 100)).toFixed(2))}% `}</strong>
                                {`(${data.maleNumerator.toLocaleString()} of ${data.allDenominator.toLocaleString()})`}
                              </span>
                            )}
                          </div>
                        );
                      }}
                      
                      // Custom Markers
                      markers={[
                        {
                          axis: 'y',
                          value: 0,
                          lineStyle: {
                            stroke: "#9eb2d4",
                            strokeWidth: 1,
                            strokeDasharray: "2 1"
                          },
                        }
                      ]}
                      
                      // Custom Layers
                      layers={[
                        refsLayer,
                        "legends",
                        "bars",
                        "markers",
                        "axes",
                      ]}
                    />
                  </div>
                </div></div>
              ))}
          </div>
          <CipModalDialog isOpen={isOpen} onClose={closeModal} />
        </div>
      </div>
    </div>
  );
};